import React from 'react';
import { Switch as MuiSwitch } from '@mui/material';
import { MinusIcon, PlusIcon } from '../../shared/Icons';
import {
  GroupBlock,
  GroupCard,
  GroupFooter,
  GroupHeader,
  IconTile,
  Row,
  Segmented,
  SegmentedOption,
  Stepper,
} from './SlotBookingEditor.styles';

interface SettingsGroupProps {
  title?: string;
  action?: React.ReactNode;
  footer?: React.ReactNode;
  footerTone?: 'danger';
  children: React.ReactNode;
}

export const SettingsGroup: React.FC<SettingsGroupProps> = ({
  title,
  action,
  footer,
  footerTone,
  children,
}) => (
  <GroupBlock>
    {(title || action) && (
      <GroupHeader>
        <span className="sb-group-title">{title}</span>
        {action}
      </GroupHeader>
    )}
    <GroupCard>{children}</GroupCard>
    {footer && <GroupFooter className={footerTone}>{footer}</GroupFooter>}
  </GroupBlock>
);

interface SettingsRowProps {
  icon?: React.ReactNode;
  iconColor?: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  stacked?: boolean;
  children?: React.ReactNode;
}

export const SettingsRow: React.FC<SettingsRowProps> = ({
  icon,
  iconColor,
  label,
  description,
  stacked,
  children,
}) => (
  <Row $stacked={stacked}>
    <div className="sb-row-main">
      {icon && iconColor && <IconTile $color={iconColor}>{icon}</IconTile>}
      <div className="sb-row-text">
        <span className="sb-row-label">{label}</span>
        {description && <span className="sb-row-description">{description}</span>}
      </div>
    </div>
    {children && (stacked ? children : <div className="sb-row-control">{children}</div>)}
  </Row>
);

interface SwitchProps {
  checked: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}

export const Switch: React.FC<SwitchProps> = ({ checked, label, onChange }) => (
  <MuiSwitch
    size="small"
    checked={checked}
    onChange={(e) => onChange(e.target.checked)}
    inputProps={{ 'aria-label': label }}
  />
);

interface SegmentedControlProps<T extends string> {
  value: T;
  options: { value: T; label: string; icon?: React.ReactNode }[];
  onChange: (value: T) => void;
}

export const SegmentedControl = <T extends string>({
  value,
  options,
  onChange,
}: SegmentedControlProps<T>) => (
  <Segmented role="tablist">
    {options.map((option) => (
      <SegmentedOption
        key={option.value}
        type="button"
        role="tab"
        aria-selected={value === option.value}
        $active={value === option.value}
        onClick={() => onChange(option.value)}
      >
        {option.icon}
        {option.label}
      </SegmentedOption>
    ))}
  </Segmented>
);

interface NumberStepperProps {
  value: number;
  min: number;
  max?: number;
  step: number;
  unit: string;
  label: string;
  onChange: (value: number) => void;
}

export const NumberStepper: React.FC<NumberStepperProps> = ({
  value,
  min,
  max,
  step,
  unit,
  label,
  onChange,
}) => {
  const clamp = (next: number) => Math.min(max ?? Infinity, Math.max(min, next));
  return (
    <Stepper>
      <button
        type="button"
        aria-label={`Decrease ${label}`}
        disabled={value <= min}
        onClick={() => onChange(clamp(value - step))}
      >
        <MinusIcon size={14} />
      </button>
      <input
        type="number"
        aria-label={label}
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(clamp(Number(e.target.value) || 0))}
      />
      <span className="sb-stepper-unit">{unit}</span>
      <button
        type="button"
        aria-label={`Increase ${label}`}
        disabled={max !== undefined && value >= max}
        onClick={() => onChange(clamp(value + step))}
      >
        <PlusIcon size={14} />
      </button>
    </Stepper>
  );
};
